import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import apiClient from '../api/client';
import { useLanguage } from '../contexts/LanguageContext';

const CreatePost = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [categories, setCategories] = useState([]);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  const loadCategories = useCallback(async () => {
    try {
      const bc = await apiClient.getCategories();
      const orig = ["房屋租售","求职招聘","美食推荐","二手交易","生活服务","社区活动"];
      const sorted = orig.map(n => bc.find(c => c.name === n)).filter(Boolean);
      bc.forEach(c => { if (!orig.includes(c.name)) sorted.push(c); });
      setCategories(sorted);
      if (sorted.length > 0) setCategoryId(sorted[0].id);
    } catch (error) {
      console.error(error);
      const fb = [{ id:'1',name:'房屋租售'},{ id:'2',name:'求职招聘'},{ id:'3',name:'美食推荐'},{ id:'4',name:'二手交易'},{ id:'5',name:'生活服务'},{ id:'6',name:'社区活动'}];
      setCategories(fb);
      if (fb.length > 0) setCategoryId(fb[0].id);
    }
  }, []);

  const loadCurrentUser = useCallback(async () => {
    try { setCurrentUser(await apiClient.getCurrentUser()); }
    catch { navigate('/login?redirect=/forum/create'); }
  }, [navigate]);

  useEffect(() => { loadCategories(); loadCurrentUser(); }, [loadCategories, loadCurrentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !content.trim() || !categoryId) return;
    setSubmitting(true);
    try { const post = await apiClient.createPost(title, content, categoryId); navigate(`/forum/post/${post.id}`); }
    catch (error) { console.error(error); alert(t('createPost.submitFailed')); }
    finally { setSubmitting(false); }
  };

  if (!currentUser) return (
    <div className="-mt-16">
      <section className="pt-32 pb-16"><div className="max-w-4xl mx-auto px-4 text-center text-gray-500">{t('createPost.loading')}</div></section>
      <section className="py-16" />
    </div>
  );

  return (
    <div className="-mt-16">
      <section className="relative pt-28 pb-10 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-mesh opacity-50" />
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-[400px] h-[400px] bg-gradient-to-br from-emerald-600/10 to-transparent rounded-full blur-[100px]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate('/forum')} className="mb-4 flex items-center text-gray-400 hover:text-white transition-colors text-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />{t('createPost.backToForum')}
          </button>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-white">{t('createPost.title')}</h1>
        </div>
      </section>

      <section className="relative py-8 md:py-12 min-h-[60vh]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="border-gradient bg-dark-card rounded-2xl shadow-dark-card p-6 md:p-8">
            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('createPost.selectCategory')}</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} required
                  className="w-full p-3 border border-dark-border rounded-xl bg-dark-surface text-white focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none transition-all">
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('createPost.postTitle')}</label>
                <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('createPost.titlePlaceholder')} required maxLength={500}
                  className="w-full p-3 border border-dark-border rounded-xl bg-dark-surface text-white placeholder-gray-500 focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none transition-all" />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-300 mb-2">{t('createPost.content')}</label>
                <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder={t('createPost.contentPlaceholder')} rows={12} required
                  className="w-full p-3 border border-dark-border rounded-xl bg-dark-surface text-white placeholder-gray-500 focus:ring-2 focus:ring-accent/30 focus:border-accent/50 outline-none resize-none transition-all" />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => navigate('/forum')} className="px-6 py-2.5 border border-dark-border text-gray-400 rounded-full hover:bg-dark-surface hover:text-white transition-all text-sm">{t('createPost.cancel')}</button>
                <button type="submit" disabled={submitting || !title.trim() || !content.trim()}
                  className="px-6 py-2.5 bg-white text-dark rounded-full font-semibold hover:shadow-glow-white transition-all disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 text-sm">
                  <Send className="h-4 w-4" />{submitting ? t('createPost.submitting') : t('createPost.submit')}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CreatePost;
